import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { INotification, notification } from "infrastructure/notification";
import { getScreenExtraReducers } from "ui/app";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";

import { releaseMessages } from "../release.localization";

import { getReleaseDetailsInitialState, IReleaseDetailsResponse, IReleaseDetailsState, IMessage, ITerm, ITermData, Status, IMessageData } from "./ReleaseDetails.state";

const initialState = getReleaseDetailsInitialState();

export const releaseDetailsManager = createSlice({
  name: "releaseDetails",
  initialState: initialState,
  reducers: {
    loadReleaseData: (state: IReleaseDetailsState, _: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, isLoading: true };
    },
    loadReleaseDataCompleted: (
      state: IReleaseDetailsState,
      action: PayloadAction<{ releaseDetails: IReleaseDetailsResponse; terms: ITerm[]; messages: IMessage[] }>
    ): IReleaseDetailsState => {
      const release: IReleaseDetailsResponse = action.payload.releaseDetails;
      const termsData: ITermData[] = [];
      action.payload.terms.forEach(element => {
        termsData.push({ term: element, status: Status.Initial, validationIssues: [] });
      });
      const messagesData: IMessageData[] = [];
      action.payload.messages.forEach(element => {
        messagesData.push({ message: element, status: Status.Initial, validationIssues: [] });
      });
      return {
        ...state,
        isLoading: false,
        releaseId: release.releaseId,
        releaseName: release.name,
        releaseStatus: release.status,
        associatedMessages: messagesData,
        associatedTerms: termsData
      };
    },
    loadReleaseDataFailed: (state: IReleaseDetailsState, action: PayloadAction<INotification>): IReleaseDetailsState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    freezeRelease: (state: IReleaseDetailsState, _: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, isLoading: true };
    },
    unfreezeRelease: (state: IReleaseDetailsState, _: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, isLoading: true };
    },
    shipRelease: (state: IReleaseDetailsState, _: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, isLoading: true };
    },
    updateStatusCompleted: (state: IReleaseDetailsState, action: PayloadAction<INotification>): IReleaseDetailsState => {
      return {
        ...state,
        isLoading: false,
        notifications: [action.payload],
        shouldRedirect: true
      };
    },
    updateStatusFailed: (state: IReleaseDetailsState, action: PayloadAction<INotification>): IReleaseDetailsState => {
      return { ...state, isLoading: false, notifications: [action.payload] };
    },
    cancelLinkClick: (state: IReleaseDetailsState): IReleaseDetailsState => {
      return { ...state, isLoading: true, shouldRedirect: true };
    },
    changeReleaseIdField: (state: IReleaseDetailsState, action: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, releaseId: action.payload };
    },
    deleteTermsFromGrid: (state: IReleaseDetailsState): IReleaseDetailsState => {
      const selectedIds = state.selectedAssociatedTerms.map(x => x.term.termId);
      const newAssociatedTerms: ITermData[] = [];
      state.associatedTerms.forEach(element => {
        if (selectedIds.includes(element.term.termId)) {
          if (element.status === Status.Added) {
            return;
          } else {
            const newElem: ITermData = { term: element.term, status: Status.Deleted, validationIssues: [] };
            newAssociatedTerms.push(newElem);
          }
        } else {
          newAssociatedTerms.push(element);
        }
      });
      return {
        ...state,
        associatedTerms: newAssociatedTerms.sort((a, b) => (a.status > b.status ? 1 : -1)),
        selectedAssociatedTerms: [],
        notifications: [notification.warning().useMessageId(releaseMessages.info("updatedItemsNotSaved"))],
        isLoading: false
      };
    },
    deleteMessagesFromGrid: (state: IReleaseDetailsState): IReleaseDetailsState => {
      const selectedIds = state.selectedAssociatedMessages.map(x => x.message.messageId);
      const newAssociatedMessages: IMessageData[] = [];
      state.associatedMessages.forEach(element => {
        if (selectedIds.includes(element.message.messageId)) {
          if (element.status === Status.Added) {
            return;
          } else {
            const newElem: IMessageData = { message: element.message, status: Status.Deleted, validationIssues: [] };
            newAssociatedMessages.push(newElem);
          }
        } else {
          newAssociatedMessages.push(element);
        }
      });
      return {
        ...state,
        associatedMessages: newAssociatedMessages.sort((a, b) => (a.status > b.status ? 1 : -1)),
        selectedAssociatedMessages: [],
        notifications: [notification.warning().useMessageId(releaseMessages.info("updatedItemsNotSaved"))],
        isLoading: false
      };
    },
    loadPickerTerms: (state: IReleaseDetailsState, _: PayloadAction<IPickerSearchCriterion<ITerm>>) => {
      return { ...state, isLoading: true };
    },
    loadPickerTermsCompleted: (state: IReleaseDetailsState, action: PayloadAction<ITerm[]>) => {
      const selectedIds = state.associatedTerms.map(x => x.term.termId);
      const terms: ITerm[] = action.payload.filter(t => !selectedIds.includes(t.termId));
      return { ...state, isLoading: false, termsPickerData: terms };
    },
    loadPickerTermsFailed: (state: IReleaseDetailsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, termsPickerData: [], notifications: [action.payload] };
    },
    selectNewTerm: (state: IReleaseDetailsState, action: PayloadAction<ITerm[]>) => {
      const newTerms: ITermData[] = action.payload.map(function(t) {
        return { term: t, status: Status.Added, validationIssues: [] };
      });
      const newAssociatedTerms = state.associatedTerms.concat(newTerms).sort((a, b) => (a.status > b.status ? 1 : -1));
      return {
        ...state,
        associatedTerms: newAssociatedTerms,
        addedTerms: action.payload,
        newStagedTerm: [],
        notifications: [notification.warning().useMessageId(releaseMessages.info("updatedItemsNotSaved"))],
        isLoading: false
      };
    },
    selectAssociatedTerm: (state: IReleaseDetailsState, action: PayloadAction<ITermData[]>) => {
      return { ...state, selectedAssociatedTerms: action.payload };
    },
    setBanner: (state: IReleaseDetailsState, action: PayloadAction<INotification>) => {
      return { ...state, notifications: [action.payload] };
    },
    loadPickerMessages: (state: IReleaseDetailsState, _: PayloadAction<IPickerSearchCriterion<IMessage>>) => {
      return { ...state, isLoading: true };
    },
    loadPickerMessagesCompleted: (state: IReleaseDetailsState, action: PayloadAction<IMessage[]>) => {
      const selectedIds = state.associatedMessages.map(x => x.message.messageId);
      selectedIds.push(state.releaseId);
      const messages: IMessage[] = action.payload.filter(m => !selectedIds.includes(m.messageId));
      return { ...state, isLoading: false, messagesPickerData: messages };
    },
    loadPickerMessagesFailed: (state: IReleaseDetailsState, action: PayloadAction<INotification>) => {
      return { ...state, isLoading: false, messagesPickerData: [], notifications: [action.payload] };
    },
    selectNewMessages: (state: IReleaseDetailsState, action: PayloadAction<IMessage[]>) => {
      const newMessages: IMessageData[] = action.payload.map(function(m) {
        return { message: m, status: Status.Added, validationIssues: [] };
      });
      const newAssociatedMessages = state.associatedMessages.concat(newMessages).sort((a, b) => (a.status > b.status ? 1 : -1));
      return {
        ...state,
        associatedMessages: newAssociatedMessages,
        newMessages: [],
        notifications: [notification.warning().useMessageId(releaseMessages.info("updatedItemsNotSaved"))],
        isLoading: false
      };
    },
    selectExistingMessage: (state: IReleaseDetailsState, action: PayloadAction<IMessageData[]>) => {
      return { ...state, selectedAssociatedMessages: action.payload };
    },
    updateRelease: (state: IReleaseDetailsState, _: PayloadAction<string>): IReleaseDetailsState => {
      return { ...state, isLoading: true };
    },
    updateReleaseCompleted: (state: IReleaseDetailsState, action: PayloadAction<INotification>): IReleaseDetailsState => {
      return {
        ...state,
        isLoading: false,
        notifications: [action.payload],
        shouldRedirect: true
      };
    },
    updateReleaseFailed: (
      state: IReleaseDetailsState,
      action: PayloadAction<{ notifications: INotification[]; terms?: ITermData[]; messages?: IMessageData[] }>
    ): IReleaseDetailsState => {
      return {
        ...state,
        isLoading: false,
        notifications: action.payload.notifications,
        associatedTerms: action.payload.terms || state.associatedTerms,
        associatedMessages: action.payload.messages || state.associatedMessages
      };
    }
  },
  extraReducers: getScreenExtraReducers<IReleaseDetailsState>("notifications", initialState)
});
